import { useState, useCallback } from 'react';
import type { AgencyTheme } from '../../types';
import ThemePreview from './ThemePreview';
import { Palette, Paintbrush, ImageIcon, Sparkles, Layout } from 'lucide-react';

interface ThemeCustomizerProps {
  theme: AgencyTheme;
  onChange: (theme: AgencyTheme) => void;
  /** URL du logo de l'agence (ou data URL) pour extraire les couleurs */
  logoUrl?: string | null;
}

/** Convertit rgb en hex */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/** Assombrit une couleur hex */
function darkenHex(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.floor(((n >> 16) & 0xff) * (1 - factor)));
  const g = Math.max(0, Math.floor(((n >> 8) & 0xff) * (1 - factor)));
  const b = Math.max(0, Math.floor((n & 0xff) * (1 - factor)));
  return rgbToHex(r, g, b);
}

/** Éclaircit une couleur hex */
function lightenHex(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  const r2 = Math.min(255, Math.floor(r + (255 - r) * factor));
  const g2 = Math.min(255, Math.floor(g + (255 - g) * factor));
  const b2 = Math.min(255, Math.floor(b + (255 - b) * factor));
  return rgbToHex(r2, g2, b2);
}

/**
 * Extrait les couleurs dominantes du logo (pixels opaques uniquement).
 * Remplit : principale, accent, texte, fond. Ne modifie pas la couleur secondaire.
 */
function extractColorsFromImage(
  imageUrl: string,
  onSuccess: (primary: string) => void,
  onError: (message: string) => void
): void {
  const img = new Image();
  img.crossOrigin = 'anonymous';

  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      const size = 64;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        onError('Impossible de lire l\'image.');
        return;
      }
      ctx.drawImage(img, 0, 0, size, size);
      const data = ctx.getImageData(0, 0, size, size).data;
      const colorCount: Record<string, number> = {};
      const quantize = (v: number) => Math.round(v / 32) * 32;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];
        if (a < 128) continue;
        const brightness = (r + g + b) / 3;
        if (brightness < 20 || brightness > 235) continue;
        const key = `${quantize(r)},${quantize(g)},${quantize(b)}`;
        colorCount[key] = (colorCount[key] || 0) + 1;
      }

      const sorted = Object.entries(colorCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([key]) => {
          const [r, g, b] = key.split(',').map(Number);
          return rgbToHex(r, g, b);
        });

      if (sorted.length === 0) {
        onError('Aucune couleur détectée. Essayez un logo avec des couleurs visibles.');
        return;
      }
      const primary = sorted[0];
      onSuccess(primary);
    } catch (e) {
      onError('Erreur lors de l\'extraction (image peut-être protégée).');
    }
  };

  img.onerror = () => onError('Impossible de charger le logo.');
  img.src = imageUrl;
}

const fontOptions = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Montserrat', label: 'Montserrat' },
];

const headerStyles = [
  { value: 'default', label: 'Défaut' },
  { value: 'centered', label: 'Centré' },
  { value: 'minimal', label: 'Minimaliste' },
];

/** Palettes prédéfinies : nom + couleurs harmonisées */
const PRESET_PALETTES: { name: string; theme: Partial<AgencyTheme> }[] = [
  {
    name: 'Bleu confiance',
    theme: {
      primaryColor: '#2563EB',
      secondaryColor: '#1E40AF',
      accentColor: '#60A5FA',
      textColor: '#1E293B',
      backgroundColor: '#F8FAFC',
    },
  },
  {
    name: 'Vert voyage',
    theme: {
      primaryColor: '#059669',
      secondaryColor: '#047857',
      accentColor: '#34D399',
      textColor: '#134E4A',
      backgroundColor: '#F0FDF4',
    },
  },
  {
    name: 'Orange soleil',
    theme: {
      primaryColor: '#EA580C',
      secondaryColor: '#C2410C',
      accentColor: '#FB923C',
      textColor: '#431407',
      backgroundColor: '#FFF7ED',
    },
  },
  {
    name: 'Violet élégant',
    theme: {
      primaryColor: '#7C3AED',
      secondaryColor: '#5B21B6',
      accentColor: '#A78BFA',
      textColor: '#3B0764',
      backgroundColor: '#F5F3FF',
    },
  },
  {
    name: 'Bordeaux premium',
    theme: {
      primaryColor: '#BE123C',
      secondaryColor: '#9F1239',
      accentColor: '#FB7185',
      textColor: '#4C0519',
      backgroundColor: '#FFF1F2',
    },
  },
  {
    name: 'Turquoise mer',
    theme: {
      primaryColor: '#0D9488',
      secondaryColor: '#0F766E',
      accentColor: '#2DD4BF',
      textColor: '#134E4A',
      backgroundColor: '#F0FDFA',
    },
  },
];

const ARRIERE_PLAN_PRESETS = [
  { label: 'Blanc', value: '#FFFFFF' },
  { label: 'Noir', value: '#1a1a1a' },
  { label: 'Gris clair', value: '#f0f0f0' },
  { label: 'Gris foncé', value: '#374151' },
] as const;

export default function ThemeCustomizer({ theme, onChange, logoUrl }: ThemeCustomizerProps) {
  const [extractError, setExtractError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);

  const handleChange = (field: keyof AgencyTheme, value: string) => {
    onChange({ ...theme, [field]: value });
  };

  const applyPalette = (palette: Partial<AgencyTheme>) => {
    onChange({ ...theme, ...palette });
  };

  const extractFromLogo = useCallback(() => {
    if (!logoUrl) {
      setExtractError('Ajoutez d\'abord un logo dans « Informations de base ».');
      return;
    }
    setExtractError(null);
    setExtracting(true);
    extractColorsFromImage(
      logoUrl,
      (primary) => {
        setExtracting(false);
        onChange({
          ...theme,
          primaryColor: primary,
          accentColor: primary,
          textColor: darkenHex(primary, 0.6),
          backgroundColor: lightenHex(primary, 0.95),
        });
      },
      (msg) => {
        setExtractError(msg);
        setExtracting(false);
      }
    );
  }, [logoUrl, theme, onChange]);

  return (
    <div className="space-y-8">
      {/* Ajuster les couleurs (1. Arrière-plan + 2. Couleurs depuis le logo + nuanciers) */}
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
          <Paintbrush className="w-4 h-4" />
          Ajuster les couleurs
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-5">
            {/* 1. Arrière-plan : compact */}
            <div className="pb-3 border-b border-gray-200">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-800 mb-1">
                <Layout className="w-3.5 h-3.5" />
                1. Arrière-plan
              </h4>
              <p className="text-xs text-gray-500 mb-2">
                Couleur de la barre en haut. Aperçu avec votre logo :
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <div
                  className="w-14 h-14 rounded-lg border border-gray-300 p-1 flex items-center justify-center shrink-0"
                  style={{ backgroundColor: theme.secondaryColor || '#FFFFFF' }}
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-gray-400">Logo</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {ARRIERE_PLAN_PRESETS.map(({ label, value }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleChange('secondaryColor', value)}
                      className={`px-2 py-1 rounded text-[11px] font-medium border transition-all ${
                        (theme.secondaryColor || '#FFFFFF') === value ? 'ring-1 ring-offset-1 ring-indigo-500' : 'border-gray-300'
                      }`}
                      style={{
                        backgroundColor: value,
                        color: value === '#FFFFFF' || value === '#f0f0f0' ? '#1f2937' : '#fff',
                      }}
                      title={value}
                    >
                      {label}
                    </button>
                  ))}
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="color"
                      value={theme.secondaryColor || '#FFFFFF'}
                      onChange={(e) => handleChange('secondaryColor', e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer border border-gray-300"
                      title="Personnalisé"
                    />
                    <span className="text-[11px] text-gray-600">Perso.</span>
                  </label>
                </div>
              </div>
            </div>
            {/* 2. Couleurs depuis votre logo */}
            <div className="pb-4 border-b border-gray-200">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
                <ImageIcon className="w-4 h-4" />
                2. Couleurs depuis votre logo
              </h4>
              <p className="text-sm text-gray-500 mb-4">
                Remplit les couleurs à partir du logo (boutons, texte du menu, texte des pages, fond). L&apos;arrière-plan n&apos;est pas modifié.
              </p>
              {logoUrl ? (
                <div className="flex flex-wrap items-center gap-4">
                  <div className="w-20 h-20 rounded-xl border-2 border-gray-200 bg-white p-1 overflow-hidden flex items-center justify-center">
                    <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                  </div>
                  <button
                    type="button"
                    onClick={extractFromLogo}
                    disabled={extracting}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {extracting ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Extraction...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Extraire les couleurs
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <p className="text-sm text-amber-700 bg-amber-50 px-4 py-3 rounded-lg">
                  Aucun logo pour le moment. Enregistrez un logo dans « Informations de base » ci-dessus.
                </p>
              )}
              {extractError && <p className="mt-3 text-sm text-red-600">{extractError}</p>}
            </div>
            {/* Nuanciers : couleurs depuis le logo */}
            <p className="text-xs font-medium text-gray-500 pt-1">Ajuster chaque couleur</p>
            {[
              { key: 'primaryColor' as const, label: 'Couleur principale' },
              { key: 'accentColor' as const, label: 'Texte du menu (liens en haut)' },
              { key: 'textColor' as const, label: 'Texte des pages' },
              { key: 'backgroundColor' as const, label: 'Fond des pages' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 w-40 shrink-0">
                  {label}
                </label>
                <input
                  type="color"
                  value={theme[key] || '#000000'}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="w-14 h-14 rounded-xl cursor-pointer border-2 border-gray-200 hover:border-gray-400 transition-colors"
                  title={theme[key]}
                />
              </div>
            ))}
          </div>
          <div className="min-w-[300px]">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Aperçu</h3>
            <ThemePreview theme={theme} />
          </div>
        </div>
      </div>

      {/* Modèles de couleurs */}
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
          <Palette className="w-4 h-4" />
          Modèles de couleurs
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Choisissez un modèle pour appliquer un jeu de couleurs en un clic.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {PRESET_PALETTES.map(({ name, theme: pal }) => (
            <button
              key={name}
              type="button"
              onClick={() => applyPalette(pal)}
              className="flex flex-col rounded-xl border-2 border-gray-200 hover:border-gray-400 hover:shadow-md transition-all p-3 text-left focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <div className="flex gap-1 rounded-lg overflow-hidden mb-2 h-8">
                <div
                  className="flex-1 min-w-0"
                  style={{ backgroundColor: pal.primaryColor || theme.primaryColor }}
                />
                <div
                  className="flex-1 min-w-0"
                  style={{ backgroundColor: pal.secondaryColor || theme.secondaryColor }}
                />
                <div
                  className="flex-1 min-w-0"
                  style={{ backgroundColor: pal.accentColor || theme.accentColor }}
                />
              </div>
              <span className="text-xs font-medium text-gray-700 truncate">{name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Présentation */}
      <div className="pt-4 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Présentation</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Présentation du menu en haut</label>
            <select
              value={theme.headerStyle || 'default'}
              onChange={(e) => handleChange('headerStyle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {headerStyles.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Style d&apos;écriture</label>
            <select
              value={theme.font || 'Inter'}
              onChange={(e) => handleChange('font', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {fontOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
